import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, Gift, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StorybookShell } from '@/components/storybook/StorybookShell';
import { StorybookBuilder } from '@/components/storybook/StorybookBuilder';
import { trackEvent, trackPageView } from '@/lib/analytics';
import {
  StorybookApiError,
  getStorybookConfig,
  lookupStorybookGift,
  type StorybookConfig,
  type StorybookGift,
} from '@/lib/storybook-api';

type State = 'loading' | 'ready' | 'not_found' | 'error';

/**
 * The parent's gift link from the email (/storybook/gift/redeem?code=...):
 * who gave the book and their message, then the usual builder, whose book is
 * created already paid by the gift. The parent still approves every picture.
 */
export default function StorybookGiftRedeem() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const code = (params.get('code') || '').trim();
  const [gift, setGift] = useState<StorybookGift | null>(null);
  const [config, setConfig] = useState<StorybookConfig | null>(null);
  const [state, setState] = useState<State>(code ? 'loading' : 'not_found');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    trackPageView('storybook_gift_redeem');
  }, []);

  useEffect(() => {
    if (!code) return undefined;
    const controller = new AbortController();
    setState('loading');
    Promise.all([lookupStorybookGift(code, controller.signal), getStorybookConfig(controller.signal)])
      .then(([g, c]) => {
        setGift(g);
        setConfig(c);
        setState('ready');
        trackEvent('storybook_gift_opened', { status: g.status, product: g.product });
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState(err instanceof StorybookApiError && err.status === 404 ? 'not_found' : 'error');
      });
    return () => controller.abort();
  }, [code, reloadKey]);

  // The giver chose the family's language; switch the page to it once.
  useEffect(() => {
    if (gift && !i18n.language.startsWith(gift.language)) void i18n.changeLanguage(gift.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gift?.language]);

  const usable = gift?.status === 'paid';
  const child = gift?.childName || '';

  return (
    <StorybookShell title={`${t('storybookGift.redeem.metaTitle')} | FaceShot-ChopShop`}>
      <section className="container mx-auto px-4 pb-20 pt-8 sm:pt-12">
        <div className="mx-auto max-w-4xl">
          {state === 'loading' && (
            <div role="status" className="glass-card flex items-center justify-center gap-3 p-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
              {t('storybookGift.redeem.loading')}
            </div>
          )}
          {state === 'error' && (
            <div role="alert" className="glass-card p-8 text-center">
              <p className="font-semibold">{t('storybookPage.builder.configError')}</p>
              <Button variant="outline" className="mt-5 rounded-full" onClick={() => setReloadKey((k) => k + 1)}>
                <RefreshCw aria-hidden="true" />
                {t('storybookPage.builder.retry')}
              </Button>
            </div>
          )}
          {state === 'not_found' && (
            <Notice title={t('storybookGift.redeem.notFound.title')} body={t('storybookGift.redeem.notFound.body')} />
          )}

          {state === 'ready' && gift && (
            <>
              <div className="relative overflow-hidden rounded-[2rem] bg-[linear-gradient(135deg,hsl(45_95%_88%),hsl(330_85%_92%)_55%,hsl(199_90%_90%))] p-6 text-center shadow-[var(--shadow-card)] sm:p-10" data-testid="storybook-gift-card">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-1.5 text-sm font-semibold text-primary">
                  <Gift className="h-4 w-4" aria-hidden="true" />
                  {t('storybookGift.redeem.eyebrow')}
                </span>
                <h1 className="mx-auto mt-4 max-w-2xl text-3xl font-semibold leading-tight [text-wrap:balance] sm:text-5xl">
                  {child
                    ? t('storybookGift.redeem.titleChild', { from: gift.fromName, child })
                    : t('storybookGift.redeem.title', { from: gift.fromName })}
                </h1>
                {gift.message && (
                  <figure className="mx-auto mt-6 max-w-xl rounded-2xl bg-white/85 px-5 py-4 text-left shadow-sm">
                    <blockquote className="text-lg italic leading-relaxed">“{gift.message}”</blockquote>
                    <figcaption className="mt-2 text-sm font-semibold text-muted-foreground">— {gift.fromName}</figcaption>
                  </figure>
                )}
                <p className="mx-auto mt-6 max-w-xl text-base">
                  {gift.product === 'print'
                    ? t(gift.shipsTo?.city ? 'storybookGift.redeem.coversPrintTo' : 'storybookGift.redeem.coversPrint', { city: gift.shipsTo?.city || '' })
                    : t('storybookGift.redeem.coversPdf')}
                </p>
                <p className="mx-auto mt-3 flex max-w-xl items-start justify-center gap-2 text-sm text-muted-foreground">
                  <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(160_60%_28%)]" aria-hidden="true" />
                  {t('storybookGift.redeem.approveNote')}
                </p>
              </div>

              {usable && config?.enabled && (
                <div className="mt-10">
                  <h2 className="mb-6 text-center text-2xl font-semibold sm:text-3xl">{t('storybookGift.redeem.builderTitle')}</h2>
                  <StorybookBuilder config={config} gift={{ code, fromName: gift.fromName, childName: gift.childName, email: gift.toEmail }} />
                </div>
              )}
              {usable && config && !config.enabled && (
                <Notice title={t('storybookPage.launching.title')} body={t('storybookGift.redeem.paused')} />
              )}
              {!usable && (
                <Notice
                  title={t(`storybookGift.redeem.status.${gift.status === 'redeeming' ? 'redeemed' : gift.status === 'disputed' ? 'refunded' : gift.status}.title`)}
                  body={t(`storybookGift.redeem.status.${gift.status === 'redeeming' ? 'redeemed' : gift.status === 'disputed' ? 'refunded' : gift.status}.body`)}
                />
              )}
            </>
          )}
        </div>
      </section>
    </StorybookShell>
  );
}

function Notice({ title, body }: { title: string; body: string }) {
  const { t } = useTranslation();
  return (
    <div className="glass-card mt-8 p-8 text-center" data-testid="storybook-gift-notice">
      <h2 className="text-2xl font-semibold">{title}</h2>
      <p className="mx-auto mt-3 max-w-lg text-muted-foreground">{body}</p>
      <Button asChild variant="outline" className="mt-6 min-h-[44px] rounded-full bg-card">
        <Link to="/storybook">{t('storybookGift.sent.seeBooks')}</Link>
      </Button>
    </div>
  );
}
